import {ChangeDetectionStrategy, Component, computed, inject, input} from '@angular/core';
import {TranslocoService} from '@jsverse/transloco';
import {ResultScoreBarComponent} from '../result-score-bar/result-score-bar.component';

@Component({
  selector: 'app-result-score-cell',
  imports: [ResultScoreBarComponent],
  templateUrl: './result-score-cell.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ResultScoreCellComponent {
  private readonly transloco = inject(TranslocoService);

  readonly rfiPercentage = input<number | null>(null);
  readonly rfiWeight = input<number | null>(null);
  readonly rfpPercentage = input<number | null>(null);
  readonly rfpWeight = input<number | null>(null);
  readonly rfdPercentage = input<number | null>(null);
  readonly rfdWeight = input<number | null>(null);

  readonly rfiWeightedPercent = computed(() =>
    this.calculateWeightedPercent(this.rfiPercentage(), this.rfiWeight()),
  );

  readonly rfpWeightedPercent = computed(() =>
    this.calculateWeightedPercent(this.rfpPercentage(), this.rfpWeight()),
  );

  readonly rfdWeightedPercent = computed(() =>
    this.calculateWeightedPercent(this.rfdPercentage(), this.rfdWeight()),
  );

  readonly tooltipText = computed(() => {
    const rfiPercent = this.normalizePercent(this.rfiPercentage());
    const rfpPercent = this.normalizePercent(this.rfpPercentage());
    const rfdPercent = this.normalizePercent(this.rfdPercentage());
    const rfiWeightPercent = this.normalizePercent(this.rfiWeight(), 100);
    const rfpWeightPercent = this.normalizePercent(this.rfpWeight(), 100);
    const rfdWeightPercent = this.normalizePercent(this.rfdWeight(), 100);

    const rfiWeighted = Math.round(this.rfiWeightedPercent());
    const rfpWeighted = Math.round(this.rfpWeightedPercent());
    const rfdWeighted = Math.round(this.rfdWeightedPercent());
    const total = Math.round(rfiWeighted + rfpWeighted + rfdWeighted);

    const lines: string[] = [
      this.buildBlock(
        '#5679F6FF',
        this.t('project.steps.invitationList.resultBar.tooltip.rfiTitle'),
        this.t('project.steps.invitationList.resultBar.tooltip.score', {
          value: Math.round(rfiPercent),
        }),
        this.t('project.steps.invitationList.resultBar.tooltip.weight', {
          value: Math.round(rfiWeightPercent),
        }),
        this.t('project.steps.invitationList.resultBar.tooltip.subTotal', {
          result: Math.round(rfiPercent),
          weight: Math.round(rfiWeightPercent),
          weighted: rfiWeighted,
        }),
      ),
    ];

    if (this.rfpWeight() !== null || this.rfpPercentage() !== null) {
      lines.push(
        this.buildBlock(
          '#8fa6ff',
          this.t('project.steps.invitationList.resultBar.tooltip.rfpTitle'),
          this.t('project.steps.invitationList.resultBar.tooltip.score', {
            value: Math.round(rfpPercent),
          }),
          this.t('project.steps.invitationList.resultBar.tooltip.weight', {
            value: Math.round(rfpWeightPercent),
          }),
          this.t('project.steps.invitationList.resultBar.tooltip.subTotal', {
            result: Math.round(rfpPercent),
            weight: Math.round(rfpWeightPercent),
            weighted: rfpWeighted,
          }),
        ),
      );
    }

    if (this.rfdWeight() !== null || this.rfdPercentage() !== null) {
      lines.push(
        this.buildBlock(
          '#b25fd7',
          this.t('project.steps.invitationList.resultBar.tooltip.rfdTitle'),
          this.t('project.steps.invitationList.resultBar.tooltip.score', {
            value: Math.round(rfdPercent),
          }),
          this.t('project.steps.invitationList.resultBar.tooltip.weight', {
            value: Math.round(rfdWeightPercent),
          }),
          this.t('project.steps.invitationList.resultBar.tooltip.subTotal', {
            result: Math.round(rfdPercent),
            weight: Math.round(rfdWeightPercent),
            weighted: rfdWeighted,
          }),
        ),
      );
    }

    lines.push(
      this.buildBlock(
        'oklch(60% 0.015 259)',
        this.t('project.steps.invitationList.resultBar.tooltip.interimResultTitle'),
        this.t('project.steps.invitationList.resultBar.tooltip.total', {total}),
        '',
      ),
    );

    return lines.join('');
  });

  private calculateWeightedPercent(percentage: number | null, weight: number | null): number {
    const normalizedPercentage = this.normalizePercent(percentage);
    const normalizedWeight = this.normalizePercent(weight);
    return normalizedPercentage * normalizedWeight;
  }

  private normalizePercent(value: number | null, multiplier = 1): number {
    if (value === null || Number.isNaN(value)) {
      return 0;
    }
    return Math.max(0, value * multiplier);
  }

  private t(key: string, params?: Record<string, number>): string {
    return this.transloco.translate(key, params ?? {});
  }

  private buildBlock(
    color: string,
    title: string,
    rfiRow: string,
    rfpRow: string,
    rfdRow?: string,
    result?: string,
  ) {
    const lineItems = [rfiRow, rfpRow, rfdRow, result].filter((line) => line && line.trim() !== '');
    const lines = lineItems
      .map(
        (line) =>
          `<div style="line-height:1.3;font-size:12px;color:var(--text-color-secondary,oklch(63% 0.02 259));white-space:nowrap;">${line}</div>`,
      )
      .join('');

    return `<div style="margin-bottom:${lines ? '6px' : '0'};"><div style="display:flex;align-items:center;gap:8px;margin-bottom:${lines ? '2px' : '0'};"><span style="display:inline-block;width:10px;height:10px;border-radius:2px;background:${color};"></span><div style="font-weight:600;line-height:1.2;">${title}</div></div>${lines ? `<div style="margin-left:18px;">${lines}</div>` : ''}</div>`;
  }
}
